import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CarForm } from './CarForm';
import { CheckCircle2, Banknote, TrendingUp, ShieldCheck, Menu, X, Phone, MapPin, ArrowUp } from 'lucide-react';
import { services, ServiceType } from '../routes/seoRoutes';
import { motion, AnimatePresence } from 'framer-motion';

// Create a custom event for scrolling to hero
export const scrollToHeroEvent = new CustomEvent('scrollToHero');

// Create a helper function for scrolling to the hero section
export const scrollToHeroSection = () => {
  const heroSection = document.getElementById('hero-section');
  if (heroSection) {
    heroSection.scrollIntoView({ behavior: 'smooth' });
  }
};

interface HeroProps {
  service?: ServiceType;
  city?: string;
}

export const Hero: React.FC<HeroProps> = ({ service, city }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();
  const heroRef = useRef<HTMLDivElement>(null);

  const scrollToHero = () => {
    scrollToHeroSection();
    setIsMenuOpen(false);
  };

  // Listen for scroll to hero events
  useEffect(() => {
    const handleScrollToHero = () => scrollToHero();
    window.addEventListener('scrollToHero', handleScrollToHero);
    return () => window.removeEventListener('scrollToHero', handleScrollToHero);
  }, []);

  // Lock scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get service config if service is provided
  const serviceConfig = service ? services[service] : null;

  const getHeroContent = () => {
    if (!service || !city) {
      return {
        title: "Fast & Hassle-Free Car Valuation",
        subtitle: "SELL YOUR CAR WITHOUT STRESS",
        description: "Get the best price for your car in 3 simple steps: Value, Inspect, and Sell. No hidden fees, no paperwork hassle, instant payment!"
      };
    }

    switch (service) {
      case 'value-your-car':
        return {
          title: `Car Valuation in ${city}`,
          subtitle: `GET YOUR CAR'S VALUE IN ${city.toUpperCase()}`,
          description: `Get a professional valuation for your car in ${city} in minutes. Fast, reliable, and stress-free service to know your vehicle's real market value.`
        };
      
      case 'sell-your-car':
        return {
          title: `Sell Your Car in ${city}`,
          subtitle: `SELL YOUR CAR IN ${city.toUpperCase()} HASSLE-FREE`,
          description: `Sell your car in ${city} without the stress. We handle everything: paperwork, inspection, and instant payment. Professional service with the best market price.`
        };

      case 'instant-car-purchase':
        return {
          title: `Quick Car Purchase in ${city}`,
          subtitle: `INSTANT CAR BUYING IN ${city.toUpperCase()}`,
          description: `Get an instant offer for your car in ${city}. No waiting, no haggling - just a fair price, same-day payment, and free vehicle collection.`
        };

      case 'car-inspection':
        return {
          title: `Car Inspection in ${city}`,
          subtitle: `PROFESSIONAL CAR INSPECTION IN ${city.toUpperCase()}`,
          description: `Professional vehicle inspection service in ${city}. Thorough assessment by certified experts, detailed report, and peace of mind.`
        };

      case 'car-valuation':
        return {
          title: `Professional Car Valuation in ${city}`,
          subtitle: `EXPERT CAR VALUATION IN ${city.toUpperCase()}`,
          description: `Get an accurate market value for your vehicle in ${city}. Our experienced automotive experts provide detailed and reliable valuations.`
        };

      case 'sell-car-now':
        return {
          title: `Sell Your Car Now in ${city}`,
          subtitle: `QUICK CAR SALE IN ${city.toUpperCase()}`,
          description: `Ready to sell your car quickly in ${city}? Get instant quotes and same-day payment. Experience a hassle-free car selling process with our professional team.`
        };

      default:
        return {
          title: serviceConfig?.title || 'Car Service',
          subtitle: `CAR SERVICE IN ${city.toUpperCase()}`,
          description: serviceConfig?.description || 'Professional car service for all your needs.'
        };
    }
  };

  const content = getHeroContent();

  const getFeatures = (serviceType?: ServiceType) => {
    switch (serviceType) {
      case 'value-your-car':
        return [
          { title: 'Free', subtitle: 'Valuation', icon: CheckCircle2 },
          { title: 'Instant', subtitle: 'Quote', icon: Banknote },
          { title: 'Best', subtitle: 'Price', icon: TrendingUp },
          { title: 'No', subtitle: 'Hassle', icon: ShieldCheck }
        ];
      
      case 'sell-your-car':
        return [
          { title: 'Same Day', subtitle: 'Payment', icon: CheckCircle2 },
          { title: 'Free', subtitle: 'Collection', icon: Banknote },
          { title: 'Best', subtitle: 'Price', icon: TrendingUp },
          { title: 'Zero', subtitle: 'Paperwork', icon: ShieldCheck }
        ];

      case 'instant-car-purchase':
        return [
          { title: 'Instant', subtitle: 'Offer', icon: CheckCircle2 },
          { title: 'Quick', subtitle: 'Payment', icon: Banknote },
          { title: 'Fair', subtitle: 'Price', icon: TrendingUp },
          { title: 'Free', subtitle: 'Collection', icon: ShieldCheck }
        ];

      case 'car-inspection':
        return [
          { title: 'Expert', subtitle: 'Assessment', icon: CheckCircle2 },
          { title: 'Detailed', subtitle: 'Report', icon: Banknote },
          { title: 'Professional', subtitle: 'Service', icon: TrendingUp },
          { title: 'Quality', subtitle: 'Guarantee', icon: ShieldCheck }
        ];

      case 'car-valuation':
        return [
          { title: 'Expert', subtitle: 'Valuation', icon: CheckCircle2 },
          { title: 'Market', subtitle: 'Analysis', icon: Banknote },
          { title: 'Accurate', subtitle: 'Price', icon: TrendingUp },
          { title: 'Detailed', subtitle: 'Report', icon: ShieldCheck }
        ];

      case 'sell-car-now':
        return [
          { title: 'Instant', subtitle: 'Quote', icon: CheckCircle2 },
          { title: 'Same Day', subtitle: 'Payment', icon: Banknote },
          { title: 'Free', subtitle: 'Collection', icon: TrendingUp },
          { title: 'Zero', subtitle: 'Hassle', icon: ShieldCheck }
        ];

      default:
        return [
          { title: 'Fast', subtitle: 'Service', icon: CheckCircle2 },
          { title: 'Instant', subtitle: 'Payment', icon: Banknote },
          { title: 'Best', subtitle: 'Price', icon: TrendingUp },
          { title: 'Zero', subtitle: 'Stress', icon: ShieldCheck }
        ];
    }
  };

  const features = getFeatures(service);

  // Update the scroll to CTA event listener
  useEffect(() => {
    const handleScrollToCTA = () => {
      const heroSection = document.getElementById('hero-section');
      if (heroSection) {
        heroSection.scrollIntoView({ behavior: 'smooth' });
      }
    };

    window.addEventListener('scrollToCTA', handleScrollToCTA);
    return () => window.removeEventListener('scrollToCTA', handleScrollToCTA);
  }, []);

  return (
    <>
      {/* Header Navigation */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md"
      >
        <div className="container px-4 mx-auto">
          <div className="flex items-center justify-between h-20">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center space-x-4"
            >
              <img src="/img/logo.png" alt="CarValue" className="h-10" />
            </motion.div>

            {/* Desktop Menu */}
            <nav className="items-center hidden space-x-8 md:flex">
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#" 
                onClick={(e) => { e.preventDefault(); scrollToHero(); }}
                className="text-gray-700 transition hover:text-blue-600"
              >
                Home
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#how-it-works" 
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-700 transition hover:text-blue-600"
              >
                How It Works
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#services" 
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-700 transition hover:text-blue-600"
              >
                Services
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#brands" 
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-700 transition hover:text-blue-600"
              >
                Brands
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#faq" 
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-700 transition hover:text-blue-600"
              >
                FAQ
              </motion.a>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                onClick={scrollToHero}
                className="px-6 py-2 text-gray-900 transition bg-[#fdc751] rounded-md hover:bg-[#fdc751]/90 font-semibold"
              >
                Get Quote
              </motion.button>
            </nav>

            {/* Mobile Menu Button */}
            <motion.button 
              whileTap={{ scale: 0.95 }}
              className="p-2 md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-50 overflow-y-auto bg-white md:hidden" 
              style={{ top: '80px' }}
            >
              <nav className="container px-4 py-6 mx-auto">
                <div className="flex flex-col space-y-4">
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#" 
                    onClick={(e) => { e.preventDefault(); scrollToHero(); }}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Home
                  </motion.a>
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#how-it-works" 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    How It Works
                  </motion.a>
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#services" 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Services
                  </motion.a>
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#brands" 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Brands
                  </motion.a>
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#faq" 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    FAQ
                  </motion.a>
                  
                  {/* Contact Information */}
                  <div className="p-4 mt-4 space-y-3 border-t border-gray-200">
                    <a 
                      href="tel:+61451326175" 
                      className="flex items-center text-gray-600 hover:text-blue-600"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      +61 451 326 175
                    </a>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-5 h-5 mr-2" />
                      Adelaide, South Australia
                    </div>
                  </div>
                  
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    onClick={() => {
                      scrollToHero();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-6 py-3 text-gray-900 transition bg-[#fdc751] rounded-md hover:bg-[#fdc751]/90 font-semibold"
                  >
                    Get Free Quote
                  </motion.button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Hero Section with dynamic content */}
      <section 
        ref={heroRef} 
        id="hero-section"
        className="relative min-h-screen w-full bg-center bg-cover bg-no-repeat pt-20 pb-20 scroll-mt-20 mt-20 md:bg-fixed" 
        style={{ 
          backgroundImage: 'url(/img/hero-home.jpg)',
        }}
      >
        {/* Mobile Background Overlay */}
        <div 
          className="absolute inset-0 md:hidden"
          style={{
            backgroundImage: 'url(/img/hero-home.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'left',
            filter: 'brightness(0.5)'
          }}
        />
        {/* Desktop Background Overlay */}
        <div className="absolute inset-0 hidden md:block bg-black opacity-50" />
        <div className="container relative z-10 px-4 mx-auto h-full flex items-center">
          <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2 w-full">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-white"
            >
              <div className="mb-8">
                <motion.span 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="block mb-4 text-lg font-semibold md:text-xl"
                >
                  {content.subtitle}
                </motion.span>
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-6 text-3xl font-bold md:text-5xl"
                >
                  {content.title}
                </motion.h1>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mb-8 text-lg text-gray-200 md:text-xl"
                >
                  {content.description}
                </motion.p>
                
                {/* Features Grid with dynamic content */}
                <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 md:gap-8 md:mb-12">
                  {features.map((feature, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center p-4 space-x-4 transition-all rounded-lg bg-white/10 backdrop-blur-sm hover:bg-white/20"
                    >
                      <feature.icon className="w-6 h-6 text-blue-400 md:w-8 md:h-8" />
                      <div>
                        <p className="text-base font-medium md:text-lg">{feature.title}</p>
                        <p className="text-sm text-gray-300 md:text-base">{feature.subtitle}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4"
              >
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={scrollToHero}
                  className="px-6 py-3 text-gray-900 transition bg-[#fdc751] rounded-md md:px-8 hover:bg-[#fdc751]/90 font-semibold flex items-center justify-center"
                >
                  <TrendingUp className="w-5 h-5 mr-2 md:w-6 md:h-6" />
                  {service ? `Value Car in ${city}` : 'Get Quote'}
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const targetSection = document.getElementById('how-it-works');
                    if (targetSection) {
                      targetSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-6 py-3 text-[#fdc751] transition bg-white rounded-md md:px-8 hover:bg-gray-100 font-semibold"
                >
                  Learn More
                </motion.button>
              </motion.div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="p-4 bg-white rounded-lg shadow-xl md:p-8"
            >
              <CarForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={scrollToHero}
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 flex items-center px-6 py-3 space-x-3 text-gray-900 transition bg-[#fdc751] rounded-full shadow-lg hover:bg-[#fdc751]/90 group font-semibold"
          >
            <span className="text-base font-medium">Get Quote Now</span>
            <ArrowUp className="w-5 h-5 transition-transform group-hover:-translate-y-1" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};